

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EnglishQuestionGroupForm from "./components/EnglishQuestionGroupForm";

import {
  useGetEnglishQuestionGroupByIdQuery,
  useUpdateEnglishQuestionGroupMutation,
} from "../../slices/englishQuestionGroupApiSlice";

const EnglishQuestionGroupEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: group,
    isLoading: isFetching,
    isError,
    error,
    refetch,
  } = useGetEnglishQuestionGroupByIdQuery(id, {
    skip: !id,
  });

  const [
    updateEnglishQuestionGroup,
    { isLoading: isUpdating },
  ] = useUpdateEnglishQuestionGroupMutation();

  const handleSubmit = async (formData) => {
    try {
      await updateEnglishQuestionGroup({
        id,
        ...formData,
      }).unwrap();

      toast.success(
        "English question group updated successfully",
      );

      navigate(`/english-questions-groups/${id}`);
    } catch (err) {
      const message =
        err?.data?.message ||
        err?.error ||
        "Failed to update English question group";

      toast.error(message);
    }
  };

  const handleCancel = () => {
    navigate(`/english-questions-groups/${id}`);
  };

  // ======================================================
  // Loading
  // ======================================================

  if (isFetching) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="animate-pulse space-y-6">
              <div className="h-7 w-64 rounded bg-slate-200" />

              <div className="h-4 w-96 max-w-full rounded bg-slate-200" />

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="h-12 rounded-lg bg-slate-200" />
                <div className="h-12 rounded-lg bg-slate-200" />
                <div className="h-32 rounded-lg bg-slate-200 md:col-span-2" />
                <div className="h-32 rounded-lg bg-slate-200 md:col-span-2" />
              </div>

              <div className="flex justify-end gap-3">
                <div className="h-10 w-24 rounded-lg bg-slate-200" />
                <div className="h-10 w-32 rounded-lg bg-slate-200" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Error
  // ======================================================

  if (isError || !group) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-7 w-7 text-red-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.29 3.86l-8.82 15a2 2 0 001.73 3h17.6a2 2 0 001.73-3l-8.82-15a2 2 0 00-3.46 0z"
                />
              </svg>
            </div>

            <h1 className="mt-5 text-xl font-semibold text-slate-900">
              Unable to load question group
            </h1>

            <p className="mt-2 text-sm text-slate-500">
              {error?.data?.message ||
                error?.error ||
                "The English question group could not be found."}
            </p>

            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => refetch()}
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
              >
                Try Again
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate("/english-questions-groups")
                }
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Back to Groups
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Main
  // ======================================================

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
                <button
                  type="button"
                  onClick={() =>
                    navigate("/english-questions-groups")
                  }
                  className="transition hover:text-indigo-600"
                >
                  English Question Groups
                </button>

                <span>/</span>

                <span className="text-slate-700">
                  Edit
                </span>
              </div>

              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Edit English Question Group
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                Update the group type, shared content, directions,
                and other group information.
              </p>
            </div>

            <button
              type="button"
              onClick={handleCancel}
              disabled={isUpdating}
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Group Summary */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                Editing Group
              </p>

              <h2 className="mt-1 text-lg font-semibold text-slate-900">
                {group.name || "Untitled Question Group"}
              </h2>

              {group.groupType && (
                <p className="mt-1 text-sm capitalize text-slate-500">
                  {String(group.groupType).replace(
                    /([A-Z])/g,
                    " $1",
                  )}
                </p>
              )}
            </div>

            {group.isActive !== undefined && (
              <span
                className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold ${
                  group.isActive
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                <span
                  className={`mr-1.5 h-1.5 w-1.5 rounded-full ${
                    group.isActive
                      ? "bg-emerald-500"
                      : "bg-slate-400"
                  }`}
                />
                {group.isActive ? "Active" : "Inactive"}
              </span>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <h2 className="text-lg font-semibold text-slate-900">
              Group Information
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Make the required changes and save the question group.
            </p>
          </div>

          <div className="p-6">
            <EnglishQuestionGroupForm
              initialData={group}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isUpdating}
              isEdit
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnglishQuestionGroupEdit;

